import { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useApi } from '../apiHelpers';
import { useNightlyReload } from '../hooks/useNightlyReload';
import OfficeMarquee from './OfficeMarquee';
import fireflyLogo from '../img/firefly-logo.png';
import LogoSpinner from '../components/ui/LogoSpinner';
import { MS, usePresence } from '../components/ui/motion';
import 'leaflet/dist/leaflet.css';

// Create a cluster-style icon with count (with optional ping animation)
function createClusterIcon(count, isPinging = false) {
  const size = Math.min(40, Math.max(20, 14 + Math.sqrt(count) * 3));

  const pingRings = isPinging ? `
    <div style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      background: rgba(255, 204, 51, 0.6);
      animation: ping-ring 1.5s ease-out infinite;
    "></div>
    <div style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      background: rgba(255, 204, 51, 0.6);
      animation: ping-ring 1.5s ease-out 0.5s infinite;
    "></div>
  ` : '';

  return L.divIcon({
    html: `<div style="position: relative; width: ${size}px; height: ${size}px;">
      ${pingRings}
      <div style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
        background: rgba(255, 204, 51, 0.9);
        border: 2px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${Math.max(10, size / 3)}px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000;
        z-index: 1;
      ">${count}</div>
    </div>`,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// Check if a location is "live" (seen in past 5 hours)
function isLive(loc) {
  if (!loc.last_seen_time) return false;
  const lastSeen = new Date(loc.last_seen_time);
  const now = new Date();
  const fiveHoursMs = 5 * 60 * 60 * 1000;
  return (now - lastSeen) < fiveHoursMs;
}

// Pre-aggregate locations into grid cells for clustering
function aggregateLocations(locations, precision = 1) {
  const clusters = new Map();

  locations.forEach((loc) => {
    const lat = parseFloat(loc.lat);
    const lon = parseFloat(loc.lon);
    if (isNaN(lat) || isNaN(lon)) return;

    // Round to grid cell
    const gridLat = Math.round(lat * precision) / precision;
    const gridLon = Math.round(lon * precision) / precision;
    const key = `${gridLat},${gridLon}`;

    const live = isLive(loc);

    if (clusters.has(key)) {
      const cluster = clusters.get(key);
      cluster.count += 1;
      // Keep centroid as average of all points
      cluster.latSum += lat;
      cluster.lonSum += lon;
      // Mark as pinging if ANY location in cluster is live
      if (live) cluster.isPinging = true;
    } else {
      clusters.set(key, {
        count: 1,
        latSum: lat,
        lonSum: lon,
        isPinging: live,
      });
    }
  });

  // Convert to array with centroid positions
  return Array.from(clusters.entries()).map(([key, data]) => ({
    position: [data.latSum / data.count, data.lonSum / data.count],
    count: data.count,
    isPinging: data.isPinging,
    key,
  }));
}

// Component to auto-fit map bounds to markers (only on initial load)
function FitBounds({ positions, hasInitialized }) {
  const map = useMap();

  useEffect(() => {
    if (hasInitialized.current || positions.length === 0) return;

    if (positions.length === 1) {
      map.setView(positions[0], 10);
    } else {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
    hasInitialized.current = true;
  }, [map, positions, hasInitialized]);

  return null;
}

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

function OfficeScreen() {
  const api = useApi();
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const overlay = usePresence(isLoading, MS.exit);
  const hasInitializedBounds = useRef(false);

  // Nightly kiosk reload at 4am to reset accumulated browser state.
  useNightlyReload(4);

  // Fetch ALL unique locations from history (everywhere we've been)
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await api.get('/fleet/all-locations');
        setLocations(data);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to fetch locations:', err);
        setIsLoading(false);
      }
    };

    fetchLocations();
    const interval = setInterval(fetchLocations, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // Pre-aggregate into clusters (grid precision of 1 = ~111km cells)
  const clusters = useMemo(() => {
    return aggregateLocations(locations, 1);
  }, [locations]);

  // Extract positions for bounds fitting
  const positions = useMemo(() => {
    return clusters.map((c) => c.position);
  }, [clusters]);

  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;

  return (
    <div className="h-screen w-screen bg-black text-white overflow-hidden relative">
      <style>{`
        @keyframes ping-ring {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.8;
          }
          100% {
            transform: translate(-50%, -50%) scale(4);
            opacity: 0;
          }
        }

        .leaflet-container {
          background: #0a0a0a;
        }

        /* Hide Leaflet attribution for cleaner TV display */
        .leaflet-control-attribution {
          display: none;
        }
      `}</style>

      {/* Marquee at top */}
      <div className="absolute top-4 left-0 right-0 z-[1000] px-4">
        <OfficeMarquee />
      </div>

      {/* Full-screen map */}
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        attributionControl={false}
        maxZoom={20}
      >
        {/* Satellite tiles */}
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxNativeZoom={18}
          maxZoom={25}
        />

        <FitBounds positions={positions} hasInitialized={hasInitializedBounds} />

        {/* Render pre-aggregated clusters directly as markers */}
        {clusters.map((cluster) => (
          <Marker
            key={cluster.key}
            position={cluster.position}
            icon={createClusterIcon(cluster.count, cluster.isPinging)}
          />
        ))}
      </MapContainer>

      {/* Logo in bottom left */}
      <div className="absolute bottom-4 left-4 z-[1000] pointer-events-none">
        <img
          src={fireflyLogo}
          alt="Firefly"
          className="h-16 object-contain"
          style={{
            filter: 'drop-shadow(0 0 20px rgba(255, 204, 51, 0.5))'
          }}
        />
      </div>

      {/* Powered by Firefleet */}
      <div className="absolute bottom-4 right-4 z-[1000] text-on-surface-variant text-sm">
        Powered by <span className="text-primary">Firefleet</span>
      </div>

      {/* Loading overlay */}
      {overlay.mounted && (
        <div
          aria-hidden={isLoading ? undefined : true}
          className={`absolute inset-0 flex items-center justify-center z-[1001] bg-black/50 transition-opacity motion-reduce:transition-none duration-exit ease-exit ${
            overlay.open ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <LogoSpinner label="Loading drone locations" />
        </div>
      )}
    </div>
  );
}

export default OfficeScreen;
